import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUrl';
import Header from '../components/Header';
import { getPasseioLang } from '../utils/passeioLang';

function PasseioDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [passeio, setPasseio] = useState(null);
  const [whatsappGlobal, setWhatsappGlobal] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const fetchPasseio = useCallback(async () => {
    try {
      const response = await api.get(`/passeios/${id}`);
      setPasseio(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }, [id]);

  const fetchWhatsappGlobal = useCallback(async () => {
    try {
      const response = await api.get('/configuracoes/whatsapp');
      setWhatsappGlobal(response.data.whatsapp || '');
    } catch (err) {
      console.error('Erro ao buscar WhatsApp global:', err);
    }
  }, []);

  useEffect(() => {
    fetchPasseio();
    fetchWhatsappGlobal();
  }, [fetchPasseio, fetchWhatsappGlobal]);

  // Auto-rolagem do carrossel
  useEffect(() => {
    if (!passeio || !passeio.Imagens || passeio.Imagens.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % passeio.Imagens.length
      );
    }, 4000); // Muda a imagem a cada 4 segundos

    return () => clearInterval(interval);
  }, [passeio]);

  // Resetar índice quando o passeio mudar
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [passeio?.id]);

  // Obter passeio no idioma correto
  const passeioLang = passeio ? getPasseioLang(passeio, i18n.language) : null;

  const handleWhatsAppClick = () => {
    if (!passeioLang) return;
    // Usar WhatsApp do passeio, ou WhatsApp global, ou número padrão
    const numeroWhatsApp = passeio?.whatsapp || whatsappGlobal;
    
    if (!numeroWhatsApp) {
      alert('Número do WhatsApp não configurado. Entre em contato com o administrador.');
      return;
    }

    // Formatar mensagem usando dados traduzidos
    const mensagem = `Olá! Tenho interesse no passeio *${passeioLang.nome}*.\n\n` +
      `📍 ${t('tourDetail.location')}: ${passeioLang.localizacao}\n` +
      `${passeioLang.duracao ? `⏱️ ${t('tourDetail.duration')}: ${passeioLang.duracao}\n` : ''}` +
      `👥 ${t('tourDetail.capacity')}: ${passeioLang.capacidade_maxima || 50} ${t('tours.people')}\n` +
      `\nPor favor, me envie mais informações sobre disponibilidade, preços e formas de pagamento.`;

    // URL do WhatsApp Web/App
    const whatsappUrl = `https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensagem)}`;
    
    // Abrir WhatsApp
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  if (!passeio || !passeioLang) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('tourDetail.notFound')}</p>
          <Link to="/" className="text-blue-600 hover:underline">
            {t('tourDetail.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  const numeroWhatsApp = passeio.whatsapp || whatsappGlobal;

  const imagensPasseio = passeio.Imagens && passeio.Imagens.length > 0 
    ? passeio.Imagens 
    : passeio.imagem_url 
      ? [{ imagem_url: passeio.imagem_url }]
      : [];

  const currentImage = imagensPasseio[currentImageIndex] || imagensPasseio[0];

  const nextImage = () => {
    if (imagensPasseio.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % imagensPasseio.length
      );
    }
  };

  const prevImage = () => {
    if (imagensPasseio.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex - 1 + imagensPasseio.length) % imagensPasseio.length
      );
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      <div className="pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Layout de duas colunas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Coluna 1: Imagens - Carrossel */}
              <div className="space-y-4">
                {imagensPasseio.length > 0 ? (
                  <>
                    {/* Carrossel Principal */}
                    <div className="relative rounded-lg overflow-hidden shadow-lg group">
                      <img
                        src={getImageUrl(currentImage.imagem_url)}
                        alt={`${passeioLang.nome} - Imagem ${currentImageIndex + 1}`}
                        className="w-full h-96 object-cover object-center transition-opacity duration-500"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=600&fit=crop&q=80';
                        }}
                      />
                      
                      {/* Botões de Navegação */}
                      {imagensPasseio.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-stone-800 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            aria-label="Imagem anterior"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-stone-800 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            aria-label="Próxima imagem"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </>
                      )}

                      {/* Indicadores de Slide */}
                      {imagensPasseio.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                          {imagensPasseio.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => goToImage(index)}
                              className={`h-2 rounded-full transition-all ${
                                index === currentImageIndex
                                  ? 'w-8 bg-white'
                                  : 'w-2 bg-white/50 hover:bg-white/75'
                              }`}
                              aria-label={`Ir para imagem ${index + 1}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Miniaturas Roláveis */}
                    {imagensPasseio.length > 1 && (
                      <div className="relative">
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory">
                          {imagensPasseio.map((imagem, index) => (
                            <div
                              key={index}
                              className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden cursor-pointer border-2 transition-all snap-start ${
                                index === currentImageIndex
                                  ? 'border-orange-500 shadow-md'
                                  : 'border-transparent hover:border-stone-300'
                              }`}
                              onClick={() => goToImage(index)}
                            >
                              <img
                                src={getImageUrl(imagem.imagem_url)}
                                alt={`${passeioLang.nome} miniatura ${index + 1}`}
                                className="w-full h-full object-cover object-center"
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop&q=80';
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        <p className="text-stone-500 text-xs mt-2 text-center">
                          {currentImageIndex + 1} de {imagensPasseio.length} imagens
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-lg overflow-hidden shadow-lg">
                    <img
                      src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=600&fit=crop&q=80"
                      alt="Passeio turístico"
                      className="w-full h-96 object-cover object-center"
                    />
                  </div>
                )}
              </div>

              {/* Coluna 2: Informações */}
              <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
                <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
                  {passeioLang.nome}
                </h1>
                <p className="text-stone-600 mb-6 leading-relaxed text-lg">
                  {passeioLang.descricao}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center text-stone-700">
                    <span className="text-2xl mr-3">📍</span>
                    <div>
                      <div className="font-semibold">{t('tourDetail.location')}</div>
                      <div className="text-stone-600">{passeioLang.localizacao}</div>
                    </div>
                  </div>
                  {passeioLang.duracao && (
                    <div className="flex items-center text-stone-700">
                      <span className="text-2xl mr-3">⏱️</span>
                      <div>
                        <div className="font-semibold">{t('tourDetail.duration')}</div>
                        <div className="text-stone-600">{passeioLang.duracao}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center text-stone-700">
                    <span className="text-2xl mr-3">👥</span>
                    <div>
                      <div className="font-semibold">{t('tourDetail.capacity')}</div>
                      <div className="text-stone-600">{passeioLang.capacidade_maxima} {t('tours.people')}</div>
                    </div>
                  </div>
                  {passeio.disponivel !== undefined && (
                    <div className="flex items-center text-stone-700">
                      <span className="text-2xl mr-3">{passeio.disponivel ? '✅' : '❌'}</span>
                      <div>
                        <div className="font-semibold">Status</div>
                        <div className="text-stone-600">
                          {passeio.disponivel ? t('tourDetail.available') : t('tourDetail.unavailable')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botão WhatsApp */}
                <div className="border-t border-stone-200 pt-6">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-stone-800 mb-2">
                      {t('tourDetail.interested')}
                    </h2>
                    <p className="text-stone-600">
                      {t('tourDetail.contactMessage')}
                    </p>
                  </div>

                  <p className="text-stone-600 text-sm mb-4 text-center bg-stone-50 p-3 rounded-lg border border-stone-200">
                    {t('tourDetail.transferAndTickets')}
                  </p>
                  
                  {numeroWhatsApp ? (
                    <button
                      onClick={handleWhatsAppClick}
                      className="w-full bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                      {t('tourDetail.contactWhatsApp')}
                    </button>
                  ) : (
                    <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                      Número do WhatsApp não configurado. Entre em contato com o administrador.
                    </div>
                  )}

                  <p className="text-stone-500 text-sm mt-4 text-center">
                    {t('tourDetail.whatsappRedirect')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-10 mt-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <img
                src="/logo-coop-igu.png"
                alt="COOP-IGU - Turismo • Receptivo • Eventos"
                className="h-10 md:h-12 w-auto mb-2 object-contain"
              />
              <p className="text-stone-400 text-sm mb-1">
                {t('home.footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('home.footer.links')}</h4>
              <ul className="space-y-1 text-stone-400 text-sm">
                <li>
                  <Link to="/" className="hover:text-white transition">
                    {t('header.home')}
                  </Link>
                </li>
                <li>
                  <Link to="/passeios" className="hover:text-white transition">
                    {t('header.tours')}
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/#sobre" 
                    className="hover:text-white transition"
                  >
                    {t('header.about')}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('home.footer.socialMedia')}</h4>
              <a
                href="https://www.instagram.com/coop_igu/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-stone-400 hover:text-pink-400 transition text-sm mb-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @coop_igu
              </a>
              <div className="mt-4">
                <h4 className="font-medium mb-3">{t('home.footer.location')}</h4>
                <p className="text-stone-400 text-sm">
                  {t('home.footer.address')}
                </p>
                <p className="text-stone-400 text-sm">
                  {t('home.footer.city')}
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-6 text-center text-stone-500 text-sm">
            <p>&copy; {new Date().getFullYear()} COOP-IGU. {t('home.footer.copyright')}</p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}

export default PasseioDetail;
